import * as React from "react";
import styled from "styled-components";
import { Timer } from "./components/Timer";
import { Checklist } from "./components/Checklist";
import { Metric } from "./components/Metric";
import { AnyActionableItem } from "src/types";
import { Block } from "src/utils/blocknote";
import { Content } from "./components/Content";
import { Sync } from "src/database/sync";
import { findBlock, setChecklistItemChecked } from "src/utils/blocks";
import { v5 as uuidv5 } from "uuid";

interface Props {
  task: AnyActionableItem;
  date: string;
  updateTask(fn: (task: AnyActionableItem) => void): void;
}

interface ContentProps extends Props {
  blocks: Block[];
}

export function TaskBody(props: Props) {
  const { task } = props;
  return (
    <Container $completed={task.completed}>
      <Title>{task.name}</Title>
      <BlocknoteContent blocks={task.content} {...props} />
    </Container>
  );
}

function BlocknoteContent({ task, updateTask, blocks, date }: ContentProps) {
  const setBlockCompleted = (blockId: string, completed: boolean) => {
    updateTask((task) => {
      task.content = setChecklistItemChecked(task.content, blockId, completed);
    });
  };

  const setMetricValue = React.useCallback(
    async (blockId: string, value: string) => {
      const block = findBlock<Block>(task.content, blockId);
      if (!block || block.type !== "metric") return;
      const seriesId = block.props.seriesId;
      if (!seriesId) {
        return;
      }
      console.log("seriesId", seriesId);
      const id = uuidv5(date, seriesId);
      console.log("setting metric value", id, value);
      await Sync.upsert({
        type: "metric",
        id,
        date,
        seriesId,
        value,
      });
    },
    [date, task],
  );

  const content: React.ReactNode[] = [];
  if (blocks.length === 0) {
    return null;
  }

  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];

    const gatherRelated = <T extends Block>(item: T): T[] => {
      const items = [item];
      while (i + 1 < blocks.length) {
        const next = blocks[i + 1];
        if (next.type === item.type) {
          items.push(next as T);
          i += 1;
        } else {
          break;
        }
      }

      return items;
    };

    content.push(
      (() => {
        switch (b.type) {
          case "bulletListItem": {
            const bullets = gatherRelated(b);
            return (
              <ul key={b.id}>
                {bullets.map((b) => (
                  <li key={b.id}>
                    <Content content={b.content} />
                    <BlocknoteContent
                      date={date}
                      task={task}
                      updateTask={updateTask}
                      blocks={b.children}
                    />
                  </li>
                ))}
              </ul>
            );
          }

          case "numberedListItem": {
            const bullets = gatherRelated(b);

            return (
              <ol key={b.id}>
                {bullets.map((b) => (
                  <li key={b.id}>
                    <Content content={b.content} />
                  </li>
                ))}
              </ol>
            );
          }

          case "checkListItem": {
            const items = gatherRelated(b);

            return (
              <ChecklistItems key={b.id}>
                {items.map((item) => (
                  <Checklist
                    key={item.id}
                    completed={item.props.checked}
                    onChange={(completed) => setBlockCompleted(item.id, completed)}
                    content={item.content}
                  />
                ))}
              </ChecklistItems>
            );
          }

          case "paragraph":
            return (
              <div key={b.id}>
                <Content content={b.content} />
              </div>
            );

          case "timer":
            return <Timer key={b.id} duration={b.props.duration} isFocused />;
          case "metric":
            return (
              <Metric
                key={b.id}
                date={date}
                seriesId={b.props.seriesId}
                onSave={(val) => setMetricValue(b.id, val)}
              />
            );
          default:
            return null;
        }
      })(),
    );
  }

  return <TaskContent>{content}</TaskContent>;
}

const Title = styled.h1`
  font-size: 24px;
  font-weight: 400;
  margin: 10px 0 10px;
  padding: 0;
  color: var(--text-secondary-color);
`;

const Container = styled.div<{ $completed: boolean }>`
  flex-grow: 1;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex-shrink: 1;
  overflow: auto;
`;

const TaskContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  > * {
    margin: 0;
  }
`;

const ChecklistItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;
